import { useReducer, useState } from "react";
import { BASE_PARAMETERS, PAGE_SIZE } from "../pages/Screener/constants";
import {
  HoldingCompaniesMetricsRequest,
  SortType,
  QueryFilterType,
} from "../types/ScreenerTypes";
import { Filter } from "../types/PageFilterTypes";

enum ActionType {
  UPDATE_FILTERS = "updateFilter",
  UPDATE_METRIC_VALUE_FILTERS = "updateMetricValueFilters",
  UPDATE_SORT = "dispatchSortUpdate",
  UPDATE_PAGINATION = "dispatchPaginationUpdate",
  UPDATE_FUND = "updateFund",
  RESET_FILTERS = "resetFilters",
}

type UpdateFiltersPayload = {
  filter: Filter[];
  columnKey: string | undefined;
};

type UpdatePaginationPayload = {
  page: number;
};

export type Action =
  | { type: ActionType.UPDATE_FILTERS; payload: UpdateFiltersPayload }
  | {
      type: ActionType.UPDATE_METRIC_VALUE_FILTERS;
      payload: QueryFilterType[];
    }
  | { type: ActionType.UPDATE_FUND; payload: Filter }
  | { type: ActionType.UPDATE_SORT; payload: SortType | null }
  | { type: ActionType.UPDATE_PAGINATION; payload: UpdatePaginationPayload }
  | { type: ActionType.RESET_FILTERS };

const reducer = (
  state: HoldingCompaniesMetricsRequest,
  action: Action
): HoldingCompaniesMetricsRequest => {
  switch (action.type) {
    case ActionType.UPDATE_FILTERS:
      return updateFilters(
        state,
        action.payload.filter,
        action.payload.columnKey
      );

    case ActionType.UPDATE_METRIC_VALUE_FILTERS:
      // Ensure metrics value filters columns are in metrics filter
      const metrics = state.filters.metrics || [];
      const validFilters = action.payload.filter((filter) =>
        metrics.includes(filter.column_key)
      );

      return {
        ...state,
        filters: {
          ...state.filters,
          metrics_value_filters: validFilters,
        },
      };
    case ActionType.UPDATE_FUND:
      return {
        ...state,
        filters: {
          ...state.filters,
          portfolio_index_id: action.payload.key as string,
          base_column_filters: state?.filters?.base_column_filters?.filter(
            (filter) => filter.column_key !== "name"
          ),
        },
        pagination: { ...state.pagination, page: 1 },
      };
    case ActionType.UPDATE_SORT:
      return {
        ...state,
        sort: action.payload ? { ...action.payload } : undefined,
        pagination: { ...state.pagination, page: 1 },
      };

    case ActionType.UPDATE_PAGINATION:
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload },
      };

    case ActionType.RESET_FILTERS:
      const updatedParameters = BASE_PARAMETERS;
      updatedParameters.filters.portfolio_index_id =
        state.filters.portfolio_index_id;
      return updatedParameters;
    default:
      console.error("Unhandled action type");
      return state;
  }
};

export const useUpdateScreenerParams =
  (): UseUpdateScreenerParamsReturnType => {
    const [state, dispatch] = useReducer(reducer, BASE_PARAMETERS);
    const [screenerTableKey, setScreenerTableKey] = useState<number>(1);

    // Helper function to update filters
    const dispatchFilterUpdate = (
      filter: Filter[],
      columnKey: string | undefined
    ): void => {
      dispatch({
        type: ActionType.UPDATE_FILTERS,
        payload: { filter, columnKey },
      });
    };

    const dispatchMetricValueFiltersUpdate = (
      filters: QueryFilterType[]
    ): void => {
      dispatch({
        type: ActionType.UPDATE_METRIC_VALUE_FILTERS,
        payload: filters,
      });
    };

    const dispatchFundUpdate = (filter: Filter): void => {
      dispatch({ type: ActionType.UPDATE_FUND, payload: filter });
    };

    // Helper function to update sort
    const dispatchSortUpdate = (sort: SortType | null): void => {
      dispatch({ type: ActionType.UPDATE_SORT, payload: sort });
    };

    // Helper function to update pagination
    const dispatchPaginationUpdate = (page: number): void => {
      dispatch({ type: ActionType.UPDATE_PAGINATION, payload: { page } });
    };

    const dispatchResetFilters = (): void => {
      setScreenerTableKey((prevKey) => prevKey + 1);
      dispatch({ type: ActionType.RESET_FILTERS });
    };

    return {
      parameters: state,
      screenerTableKey,
      dispatchResetFilters,
      dispatchFilterUpdate,
      dispatchFundUpdate,
      dispatchSortUpdate,
      dispatchPaginationUpdate,
      dispatchMetricValueFiltersUpdate,
    };
  };

export type UseUpdateScreenerParamsReturnType = {
  parameters: HoldingCompaniesMetricsRequest;
  screenerTableKey: number;
  dispatchFilterUpdate: (
    filter: Filter[],
    selectorName: string | undefined
  ) => void;
  dispatchMetricValueFiltersUpdate: (filters: QueryFilterType[]) => void;
  dispatchFundUpdate: (filter: Filter) => void;
  dispatchSortUpdate: (sort: SortType | null) => void;
  dispatchPaginationUpdate: (page: number) => void;
  dispatchResetFilters: () => void;
};

// Helper function to process and update filters
const updateFilters = (
  state: HoldingCompaniesMetricsRequest,
  filters: Filter[],
  columnKey: string | undefined
): HoldingCompaniesMetricsRequest => {
  if (!columnKey) {
    return state;
  }

  if (columnKey === "metric_id") {
    const metricKeys = filters.map((f) => f.key as string);

    return {
      ...state,
      filters: {
        ...state.filters,
        metrics: [...BASE_PARAMETERS.filters.metrics, ...metricKeys],
        metrics_value_filters:
          state.filters.metrics_value_filters?.filter((f) =>
            metricKeys.includes(f.column_key)
          ) ?? [],
      },
      pagination: {
        ...state.pagination,
        page_size: PAGE_SIZE,
      },
    };
  }

  const currentBaseColumnFilters = state.filters?.base_column_filters || [];
  let updatedFilters: QueryFilterType[];

  // Remove the filter if it already exists
  const filtersWithoutUpdatedColumnKey: QueryFilterType[] =
    currentBaseColumnFilters.filter(
      (filter) => filter.column_key !== columnKey
    );
  // Extract the key from each filter to get a string array
  const values = filters.map((filter) => filter.key as string);

  filters.length
    ? (updatedFilters = [
        ...filtersWithoutUpdatedColumnKey,
        {
          column_key: columnKey,
          values,
        },
      ])
    : (updatedFilters = filtersWithoutUpdatedColumnKey);

  // Merge the updated filters with the existing ones
  return {
    ...state,
    filters: {
      ...state.filters,
      base_column_filters: updatedFilters,
    },
    pagination: { ...state.pagination, page: 1 },
  };
};
